import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import moment from "moment";
import "moment/locale/es";
import React, { useEffect, useMemo, useState } from "react";
import { FACEBOOK_INSIGHT_LOADING } from "../../constants/loading.constant";
import { useAnalyticsCore } from "../../cores/analytics";
import { FacebookPagePost } from "../../cores/analytics/interface/api/get-facebook-posts";
import { Metric } from "../../cores/analytics/interface/api/get-insights";
import { analyticsApiService } from "../../cores/analytics/services/api";
import { useGlobalCore } from "../../cores/globals";
import { useMeCore } from "../../cores/me";
import { useRouterCore } from "../../cores/router";
import { FacebookAnalyticsDateRangeFilter } from "../component/analytics/FacebookAnalyticsDateRangeFilter";
import {
  buildFacebookDemographicsAndClicks,
  buildFacebookOverviewFromInsights,
  buildFacebookPublicationsAndTrend,
  filterPostsInRange,
} from "../component/analytics/facebookAnalyticsFromInsights";
import { facebookAnalyticsMock } from "../component/analytics/facebookAnalyticsMock";
import { FacebookMetricsDashboard } from "../component/analytics/FacebookMetricsDashboard";
import { FacebookIcon } from "../component/social/Facebook/FacebookIcon";
import { UpdateLoadingCard } from "../component/UpdateLoadingCard";
import Content from "../layout/Content";
import Layout from "../layout/Layout";

const toIsoDate = (raw: string) => {
  const m = moment(raw, ["MM/DD/YY", "MM/DD/YYYY", "YYYY-MM-DD"], true);
  return m.isValid() ? m.format("YYYY-MM-DD") : raw;
};

const formatFacebookPeriodLabel = (since: string, until: string) => {
  const start = moment(since, ["MM/DD/YY", "MM/DD/YYYY", "YYYY-MM-DD"], true);
  const end = moment(until, ["MM/DD/YY", "MM/DD/YYYY", "YYYY-MM-DD"], true);
  if (start.isValid() && end.isValid()) {
    return `${start.locale("es").format("D MMM YYYY")} - ${end
      .locale("es")
      .format("D MMM YYYY")}`;
  }
  return facebookAnalyticsMock.periodLabel;
};

export const FacebookAnalytics = () => {
  const { date, getFacebookInsights, facebookInsights } = useAnalyticsCore();
  const { isLoading } = useGlobalCore();
  const {
    company,
    socialSettings,
    companyPlan,
    companyRole,
    isAdmin,
    isReseller,
  } = useMeCore();
  const { goToSocial } = useRouterCore();

  const [feedPosts, setFeedPosts] = useState<FacebookPagePost[]>([]);
  const [previousFeedPosts, setPreviousFeedPosts] = useState<FacebookPagePost[]>(
    []
  );
  const [previousSince, setPreviousSince] = useState(date.since);
  const [previousUntil, setPreviousUntil] = useState(date.until);
  const [publicationsLoading, setPublicationsLoading] = useState(false);
  const [feedPostsLoaded, setFeedPostsLoaded] = useState(false);
  const [previousInsightsEntities, setPreviousInsightsEntities] = useState<
    Record<string, Metric>
  >({});

  const isSync = useMemo(
    () => Boolean(socialSettings && socialSettings.facebook),
    [socialSettings]
  );

  const isFutureDate = (d: string) => moment(d).isAfter(moment());
  const areDatesInverted = (since: string, until: string) =>
    moment(since).isAfter(moment(until));

  useEffect(() => {
    if (
      !companyPlan?.rules.allowAnalytics ||
      (!companyRole?.analitycs &&
        !companyRole?.owner &&
        !companyRole?.admin &&
        !companyRole?.agent &&
        !isAdmin &&
        !isReseller)
    )
      goToSocial();
  }, [goToSocial, companyPlan, companyRole, isAdmin, isReseller]);

  useEffect(() => {
    if (
      isSync &&
      company?.id &&
      !isFutureDate(date.since) &&
      !isFutureDate(date.until) &&
      !areDatesInverted(date.since, date.until)
    ) {
      void getFacebookInsights(company.id, date.since, date.until, "Daily");
      void getFacebookInsights(company.id, date.since, date.until, "Lifetime", 15);
    }
  }, [company?.id, date.since, date.until, getFacebookInsights, isSync]);

  useEffect(() => {
    if (
      !isSync ||
      !company?.id ||
      isFutureDate(date.since) ||
      isFutureDate(date.until) ||
      areDatesInverted(date.since, date.until)
    ) {
      return;
    }

    const sinceIso = toIsoDate(date.since);
    const untilIso = toIsoDate(date.until);

    setPublicationsLoading(true);
    analyticsApiService
      .facebookPublications(company.id, sinceIso, untilIso)
      .then((payload) => setFeedPosts(payload?.post || []))
      .catch((error) => {
        console.error(error);
        setFeedPosts([]);
      })
      .finally(() => {
        setPublicationsLoading(false);
        setFeedPostsLoaded(true);
      });
  }, [company?.id, date.since, date.until, isSync]);

  useEffect(() => {
    if (
      !isSync ||
      !company?.id ||
      isFutureDate(date.since) ||
      isFutureDate(date.until) ||
      areDatesInverted(date.since, date.until)
    ) {
      return;
    }

    const sinceM = moment(date.since, ["MM/DD/YY", "MM/DD/YYYY", "YYYY-MM-DD"], true);
    const untilM = moment(date.until, ["MM/DD/YY", "MM/DD/YYYY", "YYYY-MM-DD"], true);
    if (!sinceM.isValid() || !untilM.isValid()) return;

    const days = Math.max(1, untilM.diff(sinceM, "days") + 1);
    const prevUntil = sinceM.clone().subtract(1, "day").endOf("day");
    const prevSince = prevUntil.clone().subtract(days - 1, "days").startOf("day");
    const prevSinceIso = prevSince.format("YYYY-MM-DD");
    const prevUntilIso = prevUntil.format("YYYY-MM-DD");
    setPreviousSince(prevSinceIso);
    setPreviousUntil(prevUntilIso);

    analyticsApiService
      .facebookPublications(company.id, prevSinceIso, prevUntilIso)
      .then((payload) => setPreviousFeedPosts(payload?.post || []))
      .catch((error) => {
        console.error(error);
        setPreviousFeedPosts([]);
      });

    analyticsApiService
      .facebookInsights(company.id, prevSinceIso, prevUntilIso, "Daily")
      .then((metrics) => {
        const entities = (metrics || []).reduce(
          (acc: Record<string, Metric>, m: Metric) => {
            if (m?.name) acc[m.name] = m;
            return acc;
          },
          {}
        );
        setPreviousInsightsEntities(entities);
      })
      .catch((error) => {
        console.error(error);
        setPreviousInsightsEntities({});
      });
  }, [company?.id, date.since, date.until, isSync]);

  const fbSetting = socialSettings?.facebook;
  const accountName = useMemo(() => {
    const raw = fbSetting?.name?.trim();
    return raw || facebookAnalyticsMock.accountName;
  }, [fbSetting?.name]);

  const periodLabel = useMemo(
    () => formatFacebookPeriodLabel(date.since, date.until),
    [date.since, date.until]
  );

  const snapshot = useMemo(() => {
    const postsInRange = filterPostsInRange(feedPosts, date.since, date.until);
    const previousPostsInRange = filterPostsInRange(
      previousFeedPosts,
      previousSince,
      previousUntil
    );

    const liveDemographics = buildFacebookDemographicsAndClicks(
      facebookInsights,
      facebookAnalyticsMock,
      date.since,
      date.until
    );
    const liveOverview = buildFacebookOverviewFromInsights({
      entities: facebookInsights,
      previousEntities: previousInsightsEntities,
      mock: facebookAnalyticsMock,
      since: date.since,
      until: date.until,
      previousSince,
      previousUntil,
      postsInPeriod: postsInRange.length,
      previousPostsInPeriod: previousPostsInRange.length,
      postsInPeriodRows: postsInRange,
      previousPostsRows: previousPostsInRange,
    });
    const livePublications = buildFacebookPublicationsAndTrend({
      mock: facebookAnalyticsMock,
      posts: feedPosts,
      previousPosts: previousFeedPosts,
      since: date.since,
      until: date.until,
      previousSince,
      previousUntil,
      postsLoaded: feedPostsLoaded,
    });
    return {
      ...facebookAnalyticsMock,
      accountName,
      communityName: company?.name ?? accountName,
      periodLabel,
      demographics: liveDemographics.demographics,
      clicks: liveDemographics.clicks,
      growth: liveOverview.growth,
      followerBalance: liveOverview.followerBalance,
      viewedPosts: liveOverview.viewedPosts,
      publications: livePublications.publications,
    };
  }, [
    accountName,
    company?.name,
    facebookInsights,
    previousInsightsEntities,
    feedPosts,
    previousFeedPosts,
    previousSince,
    previousUntil,
    feedPostsLoaded,
    date.since,
    date.until,
    periodLabel,
  ]);

  return (
    <Layout>
      {isLoading(FACEBOOK_INSIGHT_LOADING) ? (
        <UpdateLoadingCard
          loadingId={FACEBOOK_INSIGHT_LOADING}
          text="Actualizando Métricas de Facebook"
        />
      ) : null}
      <Content>
        <div className="facebook-analytics-page-header facebook-analytics-page-header--analytics">
          <div className="facebook-analytics-page-header__toolbar">
            <div className="facebook-analytics-page-header__lead">
              <h1 className="facebook-analytics-page-header__title">
                Estadísticas de Facebook
              </h1>
              <div
                className="facebook-analytics-page-header__logo facebook-analytics-page-header__logo--subtitle"
                aria-hidden
              >
                <FontAwesomeIcon icon={["fab", "facebook-f"]} />
              </div>
            </div>
            <div className="facebook-analytics-page-header__right">
              <FacebookAnalyticsDateRangeFilter />
              <div className="facebook-analytics-page-header__account">
                <span className="facebook-analytics-page-header__facebook-badge">
                  <FacebookIcon active size="sm" />
                </span>
                <span className="facebook-analytics-page-header__company-name">
                  {snapshot.communityName}
                </span>
              </div>
            </div>
          </div>
        </div>
        <FacebookMetricsDashboard
          snapshot={snapshot}
          publicationsLoading={publicationsLoading}
        />
      </Content>
    </Layout>
  );
};
